// src/components/WhatsAppCloudChatWindow.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  db,
  collection,
  query,
  onSnapshot,
  orderBy,
  limit,
} from "../firebase";
import { toast } from "react-toastify";

const fmtTime = (ms) => {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleTimeString("es-AR", { timeStyle: "short" });
  } catch {
    return "";
  }
};

function getMessageText(m) {
  return (
    m?.text ||
    m?.body ||
    m?.message ||
    m?.content?.text ||
    (typeof m?.content === "string" ? m.content : "") ||
    ""
  );
}

export default function WhatsAppCloudChatWindow({ companyId, phoneNumberId, waId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const bottomRef = useRef(null);

  const messagesPath = useMemo(() => {
    if (!companyId || !phoneNumberId || !waId) return null;
    return `companies/${companyId}/whatsapp_numbers/${phoneNumberId}/conversations/${waId}/messages`;
  }, [companyId, phoneNumberId, waId]);

  useEffect(() => {
    if (!messagesPath) return;

    setLoading(true);

    const q = query(
      collection(db, messagesPath),
      orderBy("createdAt", "asc"),
      limit(500)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMessages(data);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
        toast.error("Error al cargar messages");
      }
    );

    return () => unsub();
  }, [messagesPath]);

  useEffect(() => {
    // autoscroll al final
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  if (!messagesPath) {
    return <div className="p-6 text-sm opacity-70">Faltan parámetros para cargar el chat.</div>;
  }

  return (
    <div className="h-full flex flex-col bg-base-200">
      <div className="px-4 py-2 border-b bg-base-100 text-xs opacity-70">
        <div className="flex items-center justify-between gap-2">
          <div>
            <span className="font-semibold">wa_id:</span> <code>{waId}</code>
          </div>
          <div className="truncate">
            <code>{messagesPath}</code>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-10 gap-2">
            <span className="loading loading-spinner loading-md" />
            <span className="text-sm opacity-70">Cargando mensajes...</span>
          </div>
        ) : messages.length ? (
          <div className="space-y-3">
            {messages.map((m) => {
              const dir = String(m?.direction || "").toLowerCase(); // in/out
              const isOut = dir === "out";
              const text = getMessageText(m);
              const ts = m?.createdAt || m?.ts || null;

              return (
                <div
                  key={m.id}
                  className={`flex ${isOut ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[82%] rounded-2xl px-4 py-2 shadow-sm border ${
                      isOut
                        ? "bg-primary text-primary-content border-primary/20"
                        : "bg-base-100 border-base-300"
                    }`}
                  >
                    <div className="text-sm whitespace-pre-wrap break-words">
                      {text || <span className="opacity-60">(sin texto)</span>}
                    </div>

                    <div className={`mt-1 text-[11px] opacity-70 ${isOut ? "text-right" : ""}`}>
                      {fmtTime(ts)}
                      {m?.bot ? " · bot" : ""}
                      {m?.type ? ` · ${m.type}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        ) : (
          <div className="text-sm opacity-70">No hay mensajes todavía.</div>
        )}
      </div>
    </div>
  );
}
